import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useInplaceEdit } from '../../contexts/component-details-page-inplace-edit/ComponentDetailsPageInplaceEditContext';
import { useGetComponentDetails } from '../../hooks/api/components';
import { useAuth } from '../../hooks/auth/useAuth';
import useDialogContext from '../../hooks/dialog/useDialogContext';
import useNotificationContext from '../../hooks/notification/useNotificationContext';
import { AppRoutes } from '../../shared/enums/app-routes';
import { ToastSeverity } from '../../shared/enums/toast-severity';
import BlurredComponentDetailsPage from './BlurredComponentDetailsPage';
import ComponentBreadCrumb from './components/component-breadcrumb/ComponentBreadCrumb';
import ComponentNameSection from './components/component-name-section/ComponentNameSection';
import ComponentTabView from './components/component-tab-view/ComponentTabView';

const ComponentDetailsPage = (): JSX.Element => {
  // Navigate to products page for BASF User
  const { isBasfUser, isCompanyAdmin } = useAuth();

  if (isBasfUser) {
    return <Navigate to={AppRoutes.Products} replace />;
  }
  // Get productId from URL
  const { componentId } = useParams() as { componentId: string };

  // Destructure the inplace edit context
  const { setComponentId: setContextComponentId } = useInplaceEdit();

  const queryClient = useQueryClient();
  const { openDialog } = useDialogContext();
  const { notify } = useNotificationContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Fetch JSON data from Pacific API
  const {
    isLoading,
    isError,
    data: componentDetails,
  } = useGetComponentDetails(componentId, {
    retry: 1,
    onSuccess: () => {
      // Invalidate components list since visiting component details page will update the status to completed
      queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey[0] === 'components' && typeof query.queryKey[1] === 'object';
        },
      });
    },
  });

  // Set the componentId in the inplace edit context
  useEffect(() => {
    setContextComponentId(componentId);
  }, [componentId, setContextComponentId]);

  const isRestricted = useMemo(() => {
    return componentDetails?.restricted ?? false;
  }, [componentDetails]);

  const isTermsAcceptanceRequired = useMemo(() => {
    return componentDetails?.termsAcceptanceRequired ?? false;
  }, [componentDetails]);

  /*
   * The components table sends its own URL along as `state.from` when opening a
   * component, so returning to it keeps the filters that URL carried. Arriving
   * any other way (a pasted link, browser history, another details page) leaves
   * nothing worth restoring, and a bare table is the safe destination.
   */
  const returnPath = useMemo(() => {
    const previousPath = (location.state?.from as string) ?? '';

    return previousPath.split('?')[0] === AppRoutes.Components
      ? previousPath
      : AppRoutes.Components;
  }, [location.state]);

  useEffect(() => {
    if (!componentDetails?.termsAcceptanceRequired) {
      return;
    }

    // Only a company admin can accept the provider's terms on behalf of the
    // whole company.
    if (isCompanyAdmin) {
      // Replaced rather than pushed: this page is unusable without the
      // accepted terms, so going back to it would only bounce the user out again.
      openDialog('accept-data-usage-terms-dialog', {
        productId: componentId,
        onDecline: () => {
          navigate(returnPath, { replace: true });
        },
      });

      return;
    }

    // Everyone else has nothing to do here until an admin has accepted.
    notify({
      severity: ToastSeverity.WARN,
      detail: t('toastMessages.dataUsageTermsAcceptancePending', {
        providerCompanyName: componentDetails.providerCompanyName,
      }),
    });
    navigate(returnPath, { replace: true });
    // openDialog is intentionally omitted: DialogProvider recreates it on every
    // open/close, so including it here would retrigger this effect in a loop.
  }, [
    componentDetails?.termsAcceptanceRequired,
    componentDetails?.providerCompanyName,
    isCompanyAdmin,
    componentId,
    navigate,
    returnPath,
    notify,
    t,
  ]);

  if (
    isError ||
    (!isLoading && !(componentDetails || Object.keys(componentDetails).length === 0))
  ) {
    return <Navigate to='/404' replace={true} />;
  }

  return (
    <div className='px-6 py-5 flex flex-column gap-2'>
      {/* Breadcrumb */}
      <ComponentBreadCrumb isLoading={isLoading} componentName={componentDetails?.productName} />

      {/* Component Name Section */}
      <ComponentNameSection
        isLoading={isLoading}
        mainProductName={componentDetails?.productName}
        ownProductName={componentDetails?.ownProductName}
      />

      <div className='flex flex-column w-full'>
        {isRestricted || isTermsAcceptanceRequired ? (
          // The data stays hidden behind the terms dialog until it is accepted,
          // but that is not a license restriction, so no upgrade prompt.
          <BlurredComponentDetailsPage showRestrictedAccessCard={isRestricted} />
        ) : (
          <ComponentTabView />
        )}
      </div>
    </div>
  );
};

export default ComponentDetailsPage;
